import { Host, h } from "@stencil/core";
import { DISABLED_CLASS, SWITCH_PARTS_DICTIONARY } from "../../common/reserved-names";
import { tokenMap } from "../../common/utils";
import { getElementInternalsLabel } from "../../common/analysis/accessibility";
/**
 * @status experimental
 *
 * A switch/toggle control that enables you to select between options.
 *
 * @part track - The track of the switch element.
 * @part thumb - The thumb of the switch element.
 * @part caption - The caption (checked or unchecked) of the switch element.
 *
 * @part checked - Present in the `track`, `thumb` and `caption` parts when the control is checked (`value` === `checkedValue`).
 * @part disabled - Present in the `track`, `thumb` and `caption` parts when the control is disabled (`disabled` === `true`).
 * @part unchecked - Present in the `track`, `thumb` and `caption` parts when the control is unchecked (`value` === `unCheckedValue`).
 */
export class ChSwitch {
    constructor() {
        this.accessibleName = undefined;
        this.checkedCaption = undefined;
        this.checkedValue = undefined;
        this.disabled = false;
        this.name = undefined;
        this.unCheckedCaption = undefined;
        this.unCheckedValue = undefined;
        this.value = null;
    }
    #accessibleNameFromExternalLabel;
    #inputRef;
    internals;
    el;
    handleValueChange(newValue) {
        // Update form value
        this.internals.setFormValue(newValue?.toString());
    }
    /**
     * The 'input' event is emitted when a change to the element's value is committed by the user.
     */
    input;
    #handleInput = (event) => {
        event.stopPropagation();
        const checked = event.target.checked;
        // Toggle the value property
        this.value = checked ? this.checkedValue : this.unCheckedValue;
        this.input.emit(event);
    };
    #handleClickChange = () => {
        this.#inputRef.click();
    };
    #stopClickPropagation = (event) => {
        event.stopPropagation();
    };
    connectedCallback() {
        // Set initial value to unchecked if empty
        this.value ||= this.unCheckedValue;
        // Accessibility
        this.internals.setFormValue(this.value?.toString());
        const labels = this.internals.labels;
        this.#accessibleNameFromExternalLabel = getElementInternalsLabel(labels);
        // Report any accessibility issue. // TODO: It should take into account the "caption" properties
        // analyzeLabelExistence(
        //   this.el,
        //   "ch-switch",
        //   labels,
        //   this.#accessibleNameFromExternalLabel,
        //   this.accessibleName
        // );
    }
    render() {
        const checked = this.value === this.checkedValue;
        return (h(Host, { key: '50c7a8a95846def4e83bac790efc3c2d4c7b2617', class: this.disabled ? DISABLED_CLASS : null, onClick: !this.disabled ? this.#handleClickChange : null }, h("label", { key: '12d78bab0c4af6e4049aa5dad4c4bf629829072d', class: "wrapper-for-click-event", onClick: !this.disabled ? this.#stopClickPropagation : null }, h("div", { key: 'd1c534acf4c26bc9f04f339f5861d17c43570a0e', class: { track: true, "track--checked": checked }, part: tokenMap({
                [SWITCH_PARTS_DICTIONARY.TRACK]: true,
                [SWITCH_PARTS_DICTIONARY.DISABLED]: this.disabled,
                [SWITCH_PARTS_DICTIONARY.CHECKED]: checked,
                [SWITCH_PARTS_DICTIONARY.UNCHECKED]: !checked
            }) }, h("input", { key: '28783bde07f69abf8803dba967fff200fecc92d3', role: "switch", "aria-checked": checked ? "true" : "false", "aria-label": this.#accessibleNameFromExternalLabel ?? this.accessibleName, class: { thumb: true, "thumb--checked": checked }, part: tokenMap({
                [SWITCH_PARTS_DICTIONARY.THUMB]: true,
                [SWITCH_PARTS_DICTIONARY.DISABLED]: this.disabled,
                [SWITCH_PARTS_DICTIONARY.CHECKED]: checked,
                [SWITCH_PARTS_DICTIONARY.UNCHECKED]: !checked
            }), checked: checked, disabled: this.disabled, type: "checkbox", value: checked ? this.checkedValue : this.unCheckedValue, onInput: !this.disabled ? this.#handleInput : null, ref: el => (this.#inputRef = el) })), h("span", { key: 'bd33513adcf236a6390a3efb4df9fd54ab934f9f', "aria-hidden": "true", class: "caption", part: tokenMap({
                [SWITCH_PARTS_DICTIONARY.CAPTION]: true,
                [SWITCH_PARTS_DICTIONARY.DISABLED]: this.disabled,
                [SWITCH_PARTS_DICTIONARY.CHECKED]: checked,
                [SWITCH_PARTS_DICTIONARY.UNCHECKED]: !checked
            }) }, checked ? this.checkedCaption : this.unCheckedCaption))));
    }
    static get is() { return "ch-switch"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["switch.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["switch.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "checkedCaption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Caption displayed when the switch is 'on'"
                },
                "attribute": "checked-caption",
                "reflect": false
            },
            "checkedValue": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value when the switch is 'on'"
                },
                "attribute": "checked-value",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute allows you specify if the element is disabled.\nIf disabled, it will not trigger any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies the `name` of the control when used in a form."
                },
                "attribute": "name",
                "reflect": true
            },
            "unCheckedCaption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Caption displayed when the switch is 'off'"
                },
                "attribute": "un-checked-caption",
                "reflect": false
            },
            "unCheckedValue": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value when the switch is 'off'. If you want to not add the value when\nthe control is used in a form and it's unchecked, just let this property\nwith the default `undefined` value."
                },
                "attribute": "un-checked-value",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value of the control."
                },
                "attribute": "value",
                "reflect": false,
                "defaultValue": "null"
            }
        };
    }
    static get events() {
        return [{
                "method": "input",
                "name": "input",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "The 'input' event is emitted when a change to the element's value is committed by the user."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "value",
                "methodName": "handleValueChange"
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
